"use client";

import { useSyncExternalStore } from "react";
import { create } from "zustand";
import type { Params, ScreenKey } from "@/core/screen-meta";
import { escreverUrl, mesmaRota } from "@/core/rotas";

/* ---------- Detecção de celular ---------- */

const MQ = "(max-width: 767px)";

function subscribe(cb: () => void) {
  const mq = window.matchMedia(MQ);
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
}

export function useIsMobile() {
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(MQ).matches,
    () => false,
  );
}

/* ---------- Navegação em pilha (estilo app nativo), com a URL como fonte da verdade ----------
 * Cada aba inferior tem uma pilha de telas. As telas são as mesmas chaves do desktop (ScreenKey):
 * /app/viagem/<id> é a mesma URL nos dois modos. Empilhar faz pushState; o botão voltar do sistema
 * dispara popstate e a pilha é reconciliada com a URL (aplicarUrl).
 */

export type MTab = "inicio" | "viagens" | "documentos" | "mais";

export interface MRoute {
  key: string;
  screen: ScreenKey;
  params: Params;
}

/** Tela raiz de cada aba inferior. */
export const RAIZ_ABA: Record<MTab, ScreenKey> = { inicio: "inicio", viagens: "viagens", documentos: "documentos", mais: "menu" };

export function abaDe(screen: ScreenKey): MTab | null {
  return (Object.keys(RAIZ_ABA) as MTab[]).find((t) => RAIZ_ABA[t] === screen) ?? null;
}

interface NavState {
  tab: MTab;
  stacks: Record<MTab, MRoute[]>;
  /** Direção da última transição (anima a entrada da tela). */
  dir: "push" | "pop" | "tab";
  push: (screen: ScreenKey, params?: Params) => void;
  back: () => void;
  /** Troca a tela do topo sem empilhar (ex.: formulário novo → edição após salvar). */
  replaceTop: (screen: ScreenKey, params?: Params) => void;
  setTab: (tab: MTab, params?: Params) => void;
  /** Deixa a pilha coerente com a URL atual (carga inicial, link direto, popstate). Não mexe no histórico. */
  aplicarUrl: (screen: ScreenKey, params: Params) => void;
}

let seq = 0;
const route = (screen: ScreenKey, params: Params = {}): MRoute => ({ key: `${screen}-${++seq}`, screen, params });

const roots = (): Record<MTab, MRoute[]> => ({
  inicio: [route("inicio")],
  viagens: [route("viagens")],
  documentos: [route("documentos")],
  mais: [route("menu")],
});

export const useNav = create<NavState>((set, get) => ({
  tab: "inicio",
  stacks: roots(),
  dir: "tab",

  push: (screen, params = {}) => {
    const { tab, stacks } = get();
    set({ stacks: { ...stacks, [tab]: [...stacks[tab], route(screen, params)] }, dir: "push" });
    escreverUrl(screen, params, "push");
  },

  back: () => {
    // Passa pelo histórico para manter o botão voltar do sistema coerente.
    if (get().stacks[get().tab].length > 1) window.history.back();
  },

  replaceTop: (screen, params = {}) => {
    const { tab, stacks } = get();
    set({ stacks: { ...stacks, [tab]: [...stacks[tab].slice(0, -1), route(screen, params)] }, dir: "tab" });
    escreverUrl(screen, params, "replace");
  },

  setTab: (tab, params) => {
    const { stacks } = get();
    if (tab === get().tab && !params) {
      // Toque na aba ativa: volta à raiz.
      const raiz = stacks[tab][0];
      set({ stacks: { ...stacks, [tab]: [raiz] }, dir: "pop" });
      escreverUrl(raiz.screen, raiz.params, "push");
      return;
    }
    const raiz = stacks[tab][0];
    const novaRaiz = params ? { ...raiz, params, key: `${raiz.screen}-${++seq}` } : raiz;
    const pilha = params ? [novaRaiz] : stacks[tab];
    set({ tab, dir: "tab", stacks: { ...stacks, [tab]: pilha } });
    const topo = pilha[pilha.length - 1];
    escreverUrl(topo.screen, topo.params, "push");
  },

  aplicarUrl: (screen, params) => {
    const { tab, stacks } = get();
    const alvo = { screen, params };
    const pilha = stacks[tab];
    const topo = pilha[pilha.length - 1];
    if (mesmaRota(topo, alvo)) return;
    // Voltando: a tela está mais abaixo na pilha atual
    for (let i = pilha.length - 2; i >= 0; i--) {
      if (mesmaRota(pilha[i], alvo)) {
        set({ stacks: { ...stacks, [tab]: pilha.slice(0, i + 1) }, dir: "pop" });
        return;
      }
    }
    // Raiz de outra aba (ou a mesma raiz com outros parâmetros)
    const aba = abaDe(screen);
    if (aba) {
      const raiz = stacks[aba][0];
      const novaRaiz = mesmaRota(raiz, alvo) ? raiz : { ...raiz, params, key: `${screen}-${++seq}` };
      set({ tab: aba, dir: "tab", stacks: { ...stacks, [aba]: [novaRaiz] } });
      return;
    }
    // Avançando (botão "avançar") ou link direto: empilha sem mexer no histórico
    set({ stacks: { ...stacks, [tab]: [...pilha, route(screen, params)] }, dir: "push" });
  },
}));

export function resetNav() {
  useNav.setState({ tab: "inicio", stacks: roots(), dir: "tab" });
}

/* ---------- Instalação do PWA ---------- */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let installEvent: BeforeInstallPromptEvent | null = null;
const installListeners = new Set<() => void>();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    installEvent = e as BeforeInstallPromptEvent;
    installListeners.forEach((l) => l());
  });
  window.addEventListener("appinstalled", () => {
    installEvent = null;
    installListeners.forEach((l) => l());
  });
}

export function useInstall() {
  const disponivel = useSyncExternalStore(
    (cb) => {
      installListeners.add(cb);
      return () => installListeners.delete(cb);
    },
    () => !!installEvent,
    () => false,
  );
  const standalone = useSyncExternalStore(
    () => () => undefined,
    () => window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true,
    () => false,
  );
  const ios = useSyncExternalStore(
    () => () => undefined,
    () => /iphone|ipad|ipod/i.test(navigator.userAgent),
    () => false,
  );
  return {
    disponivel,
    standalone,
    ios,
    instalar: async () => {
      if (!installEvent) return;
      await installEvent.prompt();
      await installEvent.userChoice.catch(() => undefined);
      installEvent = null;
      installListeners.forEach((l) => l());
    },
  };
}
